"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useRef } from "react";
import { UserCircleIcon } from "@heroicons/react/24/outline";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, type DragEndEvent, useSensor, useSensors } from "@dnd-kit/core";
import { horizontalListSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TabToggle } from "@/components/common/TabToggle";
import { useSearchParams } from "next/navigation";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useWaveCurations } from "@/hooks/waves/useWaveCurations";
import { useWaveCurationReorderMutation } from "@/hooks/waves/useWaveCurationReorderMutation";
import { useApproveWaveCustomTabLabels } from "@/hooks/waves/useWaveMetadata";
import { getProfileWaveIdentity, useProfileWave } from "@/hooks/useProfileWave";
import { useWave } from "@/hooks/useWave";
import { useWaveHasPolls } from "@/hooks/useWaveHasPolls";
import { useDecisionPoints } from "@/hooks/waves/useDecisionPoints";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { Time } from "@/helpers/time";
import { useAuth } from "@/components/auth/Auth";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useContentTab, WaveVotingState, type SetActiveContentTab } from "../ContentTabContext";
import MyStreamActionTooltip from "./MyStreamActionTooltip";
import MyStreamWaveCreateCurationAction from "./tabs/MyStreamWaveCreateCurationAction";
import MyStreamWaveCurationTabMenu from "./tabs/MyStreamWaveCurationTabMenu";
interface MyStreamWaveDesktopTabsProps {
  readonly activeTab: MyStreamWaveTab;
  readonly wave: ApiWave;
  readonly setActiveTab: SetActiveContentTab;
  readonly activeCurationId: string | null;
  readonly onSelectCuration: (curationId: string | null) => void;
  readonly showCreateCurationAction?: boolean | undefined;
}
interface TabOption {
  readonly key: string;
  readonly label: string;
  readonly panelId: string;
  readonly leadingIcon?: React.ReactNode | undefined;
  readonly leadingIconTooltipId?: string | undefined;
  readonly hasIndicator?: boolean | undefined;
  readonly action?: React.ReactNode | undefined;
}
interface ApproveTabLabels {
  readonly approvals: string;
  readonly approved: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const getCurationPanelId = (curationId: string): string => `my-stream-wave-tabpanel-curation-${curationId}`;
const getCurationTabKey = (curationId: string): string => `curation:${curationId}`;
const getCurationIdFromTabKey = (key: string): string => key.replace("curation:", "");
const getProfileCurationTooltipId = (curationId: string): string => `my-stream-profile-curation-${curationId}`;
const getEffectiveProfileCurationId = ({
  curations,
  isProfileWave,
  profileCurationId
}: {
  readonly curations: readonly {
    id: string;
  }[];
  readonly isProfileWave: boolean;
  readonly profileCurationId: string | null | undefined;
}): string | null => {
  if (!isProfileWave) {
    return null;
  }
  if (profileCurationId && curations.some(curation => curation.id === profileCurationId)) {
    return profileCurationId;
  }
  return curations[0]?.id ?? null;
};
const AUTO_EXPAND_LIMIT = 5;
const TAB_LABELS: Record<MyStreamWaveTab, string> = {
  [MyStreamWaveTab.CHAT]: "Chat",
  [MyStreamWaveTab.LEADERBOARD]: "Leaderboard",
  [MyStreamWaveTab.SUBMISSIONS]: "Submissions",
  [MyStreamWaveTab.SALES]: "Sales",
  [MyStreamWaveTab.WINNERS]: "Winners",
  [MyStreamWaveTab.OUTCOME]: "Outcome",
  [MyStreamWaveTab.MY_VOTES]: "My Votes",
  [MyStreamWaveTab.POLLS]: "Polls",
  [MyStreamWaveTab.FAQ]: "FAQ"
};
const getTabLabel = ({
  approveLabels,
  isApproveWave,
  tab
}: {
  readonly approveLabels: ApproveTabLabels;
  readonly isApproveWave: boolean;
  readonly tab: MyStreamWaveTab;
}): string => {
  if (isApproveWave && tab === MyStreamWaveTab.LEADERBOARD) {
    return approveLabels.approvals;
  }
  if (isApproveWave && tab === MyStreamWaveTab.WINNERS) {
    return approveLabels.approved;
  }
  return TAB_LABELS[tab];
};
const getWaveVotingState = ({
  isUpcoming,
  isCompleted
}: {
  readonly isUpcoming: boolean;
  readonly isCompleted: boolean;
}): WaveVotingState => {
  if (isUpcoming) {
    return WaveVotingState.NOT_STARTED;
  }
  if (isCompleted) {
    return WaveVotingState.ENDED;
  }
  return WaveVotingState.ONGOING;
};
interface DesktopTabButtonProps {
  readonly option: TabOption;
  readonly activeKey: string;
  readonly onSelect: (key: string) => void;
}
function DesktopTabButton(t0) {
  const $ = _c(17);
  const {
    option,
    activeKey,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== onSelect || $[1] !== option.key) {
    t1 = () => onSelect(option.key);
    $[0] = onSelect;
    $[1] = option.key;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = activeKey === option.key;
  const t3 = `tw-relative tw-whitespace-nowrap tw-border-x-0 tw-border-b-2 tw-border-t-0 tw-border-solid tw-bg-transparent tw-py-3 tw-text-sm tw-font-medium tw-transition-all tw-duration-200 ${activeKey === option.key ? "tw-border-primary-300 tw-text-white" : "tw-border-transparent tw-text-iron-500 desktop-hover:hover:tw-text-iron-200"}`;
  let t4;
  if ($[3] !== option.label) {
    t4 = <span className="tw-leading-5">{option.label}</span>;
    $[3] = option.label;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== option.leadingIcon || $[6] !== t4) {
    t5 = <span className="tw-inline-flex tw-h-5 tw-items-center tw-gap-1 tw-align-middle tw-leading-5">{t4}{option.leadingIcon}</span>;
    $[5] = option.leadingIcon;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== option.hasIndicator) {
    t6 = option.hasIndicator && <div className="tw-absolute -tw-right-1 tw-top-1 tw-h-2 tw-w-2 tw-rounded-full tw-bg-red" />;
    $[8] = option.hasIndicator;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== option.panelId || $[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t5 || $[15] !== t6) {
    t7 = <button onClick={t1} role="tab" aria-selected={t2} aria-controls={option.panelId} className={t3}>{t5}{t6}</button>;
    $[10] = option.panelId;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function ProfileCurationIcon(t0) {
  const $ = _c(3);
  const {
    tooltipId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <UserCircleIcon aria-hidden="true" className="tw-block tw-size-3.5 tw-flex-shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== tooltipId) {
    t2 = <span aria-label="Profile curation" data-tooltip-id={tooltipId} data-tooltip-content="Profile curation" className="tw-inline-flex tw-size-3.5 tw-flex-shrink-0 tw-items-center tw-justify-center tw-leading-none tw-text-primary-300">{t1}</span>;
    $[1] = tooltipId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function ReorderHandleIcon(t0) {
  const $ = _c(8);
  const {
    className
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <circle cx="9" cy="6" r="1.5" />;
    t2 = <circle cx="15" cy="6" r="1.5" />;
    t3 = <circle cx="9" cy="12" r="1.5" />;
    t4 = <circle cx="15" cy="12" r="1.5" />;
    t5 = <circle cx="9" cy="18" r="1.5" />;
    t6 = <circle cx="15" cy="18" r="1.5" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] !== className) {
    t7 = <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>{t1}{t2}{t3}{t4}{t5}{t6}</svg>;
    $[6] = className;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
}
function DesktopTabOption(t0) {
  const $ = _c(12);
  const {
    option,
    activeKey,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== activeKey || $[1] !== onSelect || $[2] !== option) {
    t1 = <DesktopTabButton option={option} activeKey={activeKey} onSelect={onSelect} />;
    $[0] = activeKey;
    $[1] = onSelect;
    $[2] = option;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== option.leadingIconTooltipId) {
    t2 = option.leadingIconTooltipId !== undefined && <MyStreamActionTooltip id={option.leadingIconTooltipId} />;
    $[4] = option.leadingIconTooltipId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== option.action) {
    t3 = option.action !== undefined && option.action !== null && <div className="tw-border-x-0 tw-border-b-2 tw-border-t-0 tw-border-solid tw-border-transparent">{option.action}</div>;
    $[6] = option.action;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className="tw-flex tw-items-center">{t1}{t2}{t3}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function SortableCurationTabOption(t0) {
  const $ = _c(36);
  const {
    option,
    activeKey,
    isSortingDisabled,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== isSortingDisabled || $[1] !== option.key) {
    t1 = {
      id: option.key,
      disabled: isSortingDisabled
    };
    $[0] = isSortingDisabled;
    $[1] = option.key;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition
  } = useSortable(t1);
  let t2;
  if ($[3] !== transform) {
    t2 = CSS.Transform.toString(transform);
    $[3] = transform;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = isDragging ? 10 : undefined;
  const t4 = isDragging ? 0.85 : undefined;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4 || $[8] !== transition) {
    t5 = {
      transform: t2,
      transition,
      zIndex: t3,
      opacity: t4
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = transition;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const style = t5;
  const reorderTooltipId = `${option.key}-reorder-tooltip`;
  const t6 = `Drag ${option.label} curation tab`;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ReorderHandleIcon className="tw-block tw-size-4 tw-flex-shrink-0" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== attributes || $[12] !== isSortingDisabled || $[13] !== listeners || $[14] !== reorderTooltipId || $[15] !== setActivatorNodeRef || $[16] !== t6) {
    t8 = <button ref={setActivatorNodeRef} type="button" disabled={isSortingDisabled} aria-label={t6} data-tooltip-id={reorderTooltipId} data-tooltip-content="Drag to reorder" className="tw-inline-flex tw-h-8 tw-w-4 tw-flex-shrink-0 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-text-iron-600 tw-transition hover:tw-text-iron-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-40" {...attributes} {...listeners}>{t7}</button>;
    $[11] = attributes;
    $[12] = isSortingDisabled;
    $[13] = listeners;
    $[14] = reorderTooltipId;
    $[15] = setActivatorNodeRef;
    $[16] = t6;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== reorderTooltipId) {
    t9 = <MyStreamActionTooltip id={reorderTooltipId} />;
    $[18] = reorderTooltipId;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== activeKey || $[21] !== onSelect || $[22] !== option) {
    t10 = <DesktopTabButton option={option} activeKey={activeKey} onSelect={onSelect} />;
    $[20] = activeKey;
    $[21] = onSelect;
    $[22] = option;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== option.leadingIconTooltipId) {
    t11 = option.leadingIconTooltipId !== undefined && <MyStreamActionTooltip id={option.leadingIconTooltipId} />;
    $[24] = option.leadingIconTooltipId;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== option.action) {
    t12 = option.action !== undefined && option.action !== null && <div className="tw-border-x-0 tw-border-b-2 tw-border-t-0 tw-border-solid tw-border-transparent">{option.action}</div>;
    $[26] = option.action;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== setNodeRef || $[29] !== style || $[30] !== t10 || $[31] !== t11 || $[32] !== t12 || $[33] !== t8 || $[34] !== t9) {
    t13 = <div ref={setNodeRef} style={style} className="tw-flex tw-items-center">{t8}{t9}{t10}{t11}{t12}</div>;
    $[28] = setNodeRef;
    $[29] = style;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t8;
    $[34] = t9;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  return t13;
}
const MyStreamWaveDesktopTabs: React.FC<MyStreamWaveDesktopTabsProps> = t0 => {
  const $ = _c(142);
  const {
    activeTab,
    wave,
    setActiveTab,
    activeCurationId,
    onSelectCuration,
    showCreateCurationAction: t1
  } = t0;
  const showCreateCurationAction = t1 === undefined ? true : t1;
  const searchParams = useSearchParams();
  const {
    availableTabs,
    updateAvailableTabs
  } = useContentTab();
  const {
    activeProfileProxy,
    connectedProfile
  } = useAuth();
  const hasAuthenticatedProfile = Boolean(connectedProfile?.handle);
  const {
    isChatWave,
    isApproveWave,
    isMemesWave,
    isCurationWave,
    isRankWave,
    pauses: t2
  } = useWave(wave);
  const {
    filterDecisionsDuringPauses
  } = t2;
  const approveLabels = useApproveWaveCustomTabLabels(wave);
  const isCompetitionWave = isRankWave || isApproveWave;
  const {
    voting: t3,
    decisions: t4
  } = useWaveTimers(wave);
  const {
    isUpcoming,
    isCompleted
  } = t3;
  const {
    firstDecisionDone
  } = t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      initialPastWindow: 3,
      initialFutureWindow: 10
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const {
    allDecisions,
    hasMoreFuture,
    loadMoreFuture
  } = useDecisionPoints(wave, t5);
  let t6;
  if ($[1] !== wave.id) {
    t6 = {
      waveId: wave.id
    };
    $[1] = wave.id;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const {
    data: t7
  } = useWaveCurations(t6);
  const curations = t7 === undefined ? [] : t7;
  const isConnectedProfileWaveAuthor = connectedProfile?.id === wave.author.id;
  const t8 = isConnectedProfileWaveAuthor ? connectedProfile : wave.author;
  let t9;
  if ($[3] !== t8) {
    t9 = getProfileWaveIdentity(t8);
    $[3] = t8;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  const profileWaveIdentity = t9;
  const t10 = profileWaveIdentity.length > 0 && curations.length > 0;
  let t11;
  if ($[5] !== profileWaveIdentity || $[6] !== t10) {
    t11 = {
      identity: profileWaveIdentity,
      enabled: t10
    };
    $[5] = profileWaveIdentity;
    $[6] = t10;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const {
    data: profileWave
  } = useProfileWave(t11);
  let t12;
  if ($[8] !== wave.id) {
    t12 = {
      waveId: wave.id
    };
    $[8] = wave.id;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  const {
    reorderCuration,
    isPending: isCurationReorderPending
  } = useWaveCurationReorderMutation(t12);
  const canManageCurations = wave.wave.authenticated_user_eligible_for_admin === true;
  const isProfileWave = profileWave?.profile_wave_id === wave.id;
  const profileCurationId = getEffectiveProfileCurationId({
    curations,
    isProfileWave,
    profileCurationId: profileWave?.profile_curation_id
  });
  const canSetProfileCuration = isProfileWave && isConnectedProfileWaveAuthor && activeProfileProxy === null;
  let t13;
  if ($[10] !== wave.id) {
    t13 = {
      waveId: wave.id
    };
    $[10] = wave.id;
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  const hasPolls = useWaveHasPolls(t13);
  let t14;
  if ($[12] !== allDecisions || $[13] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t14 = allDecisions.filter(decision_0 => filtered.some(item => item.decision_time === decision_0.timestamp));
    $[12] = allDecisions;
    $[13] = filterDecisionsDuringPauses;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const filteredDecisions = t14;
  let t15;
  if ($[15] !== filteredDecisions) {
    t15 = filteredDecisions.find(_temp2)?.timestamp ?? null;
    $[15] = filteredDecisions;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  const nextDecisionTime = t15;
  const autoExpandFutureAttemptsRef = useRef(0);
  const desktopTabsScrollerRef = useRef(null);
  const mobileTabsScrollerRef = useRef(null);
  let t16;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      activationConstraint: {
        distance: 6
      }
    };
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      coordinateGetter: sortableKeyboardCoordinates
    };
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  const sortableSensors = useSensors(useSensor(PointerSensor, t16), useSensor(KeyboardSensor, t17));
  let t18;
  let t19;
  if ($[19] !== hasMoreFuture || $[20] !== loadMoreFuture || $[21] !== nextDecisionTime) {
    t18 = () => {
      const hasUpcoming = typeof nextDecisionTime === "number";
      if (hasUpcoming || !hasMoreFuture) {
        autoExpandFutureAttemptsRef.current = 0;
        return;
      }
      if (autoExpandFutureAttemptsRef.current >= AUTO_EXPAND_LIMIT) {
        return;
      }
      const timeoutId = globalThis.setTimeout(() => {
        autoExpandFutureAttemptsRef.current = autoExpandFutureAttemptsRef.current + 1;
        loadMoreFuture();
      }, 50);
      return () => {
        clearTimeout(timeoutId);
      };
    };
    t19 = [nextDecisionTime, hasMoreFuture, loadMoreFuture];
    $[19] = hasMoreFuture;
    $[20] = loadMoreFuture;
    $[21] = nextDecisionTime;
    $[22] = t18;
    $[23] = t19;
  } else {
    t18 = $[22];
    t19 = $[23];
  }
  useEffect(t18, t19);
  let t20;
  if ($[24] !== isCompleted || $[25] !== isUpcoming) {
    t20 = getWaveVotingState({
      isUpcoming,
      isCompleted
    });
    $[24] = isCompleted;
    $[25] = isUpcoming;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  const votingState = t20;
  let t21;
  if ($[27] !== firstDecisionDone || $[28] !== hasAuthenticatedProfile || $[29] !== hasPolls || $[30] !== isApproveWave || $[31] !== isChatWave || $[32] !== isCurationWave || $[33] !== isMemesWave || $[34] !== searchParams || $[35] !== updateAvailableTabs || $[36] !== votingState || $[37] !== wave.id) {
    t21 = () => {
      const hasSerialTarget = searchParams.get("serialNo") !== null;
      updateAvailableTabs({
        waveId: wave.id,
        isMemesWave,
        isChatWave,
        hasPolls,
        hasAuthenticatedProfile,
        isCurationWave,
        isApproveWave,
        votingState,
        hasFirstDecisionPassed: firstDecisionDone,
        transientPreferredTab: hasSerialTarget ? MyStreamWaveTab.CHAT : null
      });
    };
    $[27] = firstDecisionDone;
    $[28] = hasAuthenticatedProfile;
    $[29] = hasPolls;
    $[30] = isApproveWave;
    $[31] = isChatWave;
    $[32] = isCurationWave;
    $[33] = isMemesWave;
    $[34] = searchParams;
    $[35] = updateAvailableTabs;
    $[36] = votingState;
    $[37] = wave.id;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== firstDecisionDone || $[40] !== hasAuthenticatedProfile || $[41] !== hasPolls || $[42] !== isApproveWave || $[43] !== isChatWave || $[44] !== isCurationWave || $[45] !== isMemesWave || $[46] !== searchParams || $[47] !== updateAvailableTabs || $[48] !== votingState || $[49] !== wave) {
    t22 = [wave, isMemesWave, isChatWave, hasPolls, isApproveWave, hasAuthenticatedProfile, isCurationWave, votingState, firstDecisionDone, searchParams, updateAvailableTabs];
    $[39] = firstDecisionDone;
    $[40] = hasAuthenticatedProfile;
    $[41] = hasPolls;
    $[42] = isApproveWave;
    $[43] = isChatWave;
    $[44] = isCurationWave;
    $[45] = isMemesWave;
    $[46] = searchParams;
    $[47] = updateAvailableTabs;
    $[48] = votingState;
    $[49] = wave;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  useEffect(t21, t22);
  let t23;
  if ($[51] !== approveLabels || $[52] !== availableTabs || $[53] !== hasAuthenticatedProfile || $[54] !== isApproveWave || $[55] !== isCompetitionWave || $[56] !== isCurationWave || $[57] !== isMemesWave) {
    let t24;
    if ($[59] !== hasAuthenticatedProfile || $[60] !== isCompetitionWave || $[61] !== isCurationWave || $[62] !== isMemesWave) {
      t24 = tab => {
        if (tab === MyStreamWaveTab.MY_VOTES) {
          return isCurationWave || hasAuthenticatedProfile && (isMemesWave || isCompetitionWave);
        }
        if (tab === MyStreamWaveTab.SALES) {
          return isCurationWave;
        }
        if (tab === MyStreamWaveTab.FAQ) {
          return isMemesWave;
        }
        return true;
      };
      $[59] = hasAuthenticatedProfile;
      $[60] = isCompetitionWave;
      $[61] = isCurationWave;
      $[62] = isMemesWave;
      $[63] = t24;
    } else {
      t24 = $[63];
    }
    let t25;
    if ($[64] !== approveLabels || $[65] !== isApproveWave) {
      t25 = tab_0 => ({
        key: tab_0,
        label: getTabLabel({
          approveLabels,
          isApproveWave,
          tab: tab_0
        }),
        panelId: getContentTabPanelId(tab_0)
      });
      $[64] = approveLabels;
      $[65] = isApproveWave;
      $[66] = t25;
    } else {
      t25 = $[66];
    }
    t23 = availableTabs.filter(t24).map(t25);
    $[51] = approveLabels;
    $[52] = availableTabs;
    $[53] = hasAuthenticatedProfile;
    $[54] = isApproveWave;
    $[55] = isCompetitionWave;
    $[56] = isCurationWave;
    $[57] = isMemesWave;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  const standardOptions = t23;
  let t24;
  if ($[67] !== activeCurationId || $[68] !== canManageCurations || $[69] !== canSetProfileCuration || $[70] !== curations || $[71] !== onSelectCuration || $[72] !== profileCurationId || $[73] !== wave) {
    let t25;
    if ($[75] !== activeCurationId || $[76] !== canManageCurations || $[77] !== canSetProfileCuration || $[78] !== onSelectCuration || $[79] !== profileCurationId || $[80] !== wave) {
      t25 = curation => ({
        key: getCurationTabKey(curation.id),
        label: curation.name,
        panelId: getCurationPanelId(curation.id),
        leadingIcon: curation.id === profileCurationId ? <ProfileCurationIcon tooltipId={getProfileCurationTooltipId(curation.id)} /> : undefined,
        leadingIconTooltipId: curation.id === profileCurationId ? getProfileCurationTooltipId(curation.id) : undefined,
        action: canManageCurations ? <MyStreamWaveCurationTabMenu wave={wave} curation={curation} canSetAsProfileCuration={canSetProfileCuration && curation.id !== profileCurationId} onDeleted={activeCurationId === curation.id ? () => onSelectCuration(null) : undefined} /> : undefined
      });
      $[75] = activeCurationId;
      $[76] = canManageCurations;
      $[77] = canSetProfileCuration;
      $[78] = onSelectCuration;
      $[79] = profileCurationId;
      $[80] = wave;
      $[81] = t25;
    } else {
      t25 = $[81];
    }
    t24 = curations.map(t25);
    $[67] = activeCurationId;
    $[68] = canManageCurations;
    $[69] = canSetProfileCuration;
    $[70] = curations;
    $[71] = onSelectCuration;
    $[72] = profileCurationId;
    $[73] = wave;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  const curationOptions = t24;
  let t25;
  if ($[82] !== curationOptions || $[83] !== standardOptions) {
    t25 = [...standardOptions, ...curationOptions];
    $[82] = curationOptions;
    $[83] = standardOptions;
    $[84] = t25;
  } else {
    t25 = $[84];
  }
  const options = t25;
  let t26;
  if ($[85] !== activeCurationId || $[86] !== activeTab) {
    t26 = activeCurationId ? getCurationTabKey(activeCurationId) : activeTab;
    $[85] = activeCurationId;
    $[86] = activeTab;
    $[87] = t26;
  } else {
    t26 = $[87];
  }
  const activeKey = t26;
  let t27;
  if ($[88] !== curations) {
    t27 = curations.map(_temp3);
    $[88] = curations;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  const curationTabKeys = t27;
  const canDragCurations = canManageCurations && curations.length > 1;
  let t28;
  if ($[90] !== curations || $[91] !== reorderCuration) {
    t28 = t29 => {
      const {
        active,
        over
      } = t29;
      if (over === null || active.id === over.id) {
        return;
      }
      const curationId = getCurationIdFromTabKey(String(active.id));
      const targetIndex = curations.findIndex(curation_1 => getCurationTabKey(curation_1.id) === over.id);
      const curation_2 = curations.find(item_0 => item_0.id === curationId) ?? null;
      if (curation_2 === null || targetIndex < 0) {
        return;
      }
      reorderCuration({
        curation: curation_2,
        targetPriorityOrder: targetIndex + 1,
        curations
      });
    };
    $[90] = curations;
    $[91] = reorderCuration;
    $[92] = t28;
  } else {
    t28 = $[92];
  }
  const handleCurationDragEnd = t28;
  let t29;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => {
      const frameId = globalThis.window.requestAnimationFrame(() => {
        [desktopTabsScrollerRef.current, mobileTabsScrollerRef.current].forEach(_temp4);
      });
      return () => {
        globalThis.window.cancelAnimationFrame(frameId);
      };
    };
    $[93] = t29;
  } else {
    t29 = $[93];
  }
  let t30;
  if ($[94] !== activeKey || $[95] !== options) {
    t30 = [activeKey, options];
    $[94] = activeKey;
    $[95] = options;
    $[96] = t30;
  } else {
    t30 = $[96];
  }
  useEffect(t29, t30);
  if (isChatWave && !canManageCurations && curations.length === 0 && standardOptions.length <= 1) {
    return null;
  }
  let t31;
  if ($[97] !== onSelectCuration || $[98] !== setActiveTab) {
    t31 = key => {
      if (key.startsWith("curation:")) {
        onSelectCuration(getCurationIdFromTabKey(key));
        return;
      }
      onSelectCuration(null);
      setActiveTab(key as MyStreamWaveTab);
    };
    $[97] = onSelectCuration;
    $[98] = setActiveTab;
    $[99] = t31;
  } else {
    t31 = $[99];
  }
  let t32;
  if ($[100] !== activeKey || $[101] !== options || $[102] !== t31) {
    t32 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1 sm:tw-hidden"><div ref={mobileTabsScrollerRef} className="tw-min-w-0 tw-flex-1 tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300"><div className="tw-inline-flex tw-items-center tw-gap-1"><TabToggle options={options} activeKey={activeKey} onSelect={t31} /></div></div></div>;
    $[100] = activeKey;
    $[101] = options;
    $[102] = t31;
    $[103] = t32;
  } else {
    t32 = $[103];
  }
  let t33;
  if ($[104] !== activeKey || $[105] !== onSelectCuration || $[106] !== setActiveTab || $[107] !== standardOptions) {
    let t34;
    if ($[109] !== activeKey || $[110] !== onSelectCuration || $[111] !== setActiveTab) {
      t34 = option => <DesktopTabOption key={option.key} option={option} activeKey={activeKey} onSelect={key_0 => {
        onSelectCuration(null);
        setActiveTab(key_0 as MyStreamWaveTab);
      }} />;
      $[109] = activeKey;
      $[110] = onSelectCuration;
      $[111] = setActiveTab;
      $[112] = t34;
    } else {
      t34 = $[112];
    }
    t33 = standardOptions.map(t34);
    $[104] = activeKey;
    $[105] = onSelectCuration;
    $[106] = setActiveTab;
    $[107] = standardOptions;
    $[108] = t33;
  } else {
    t33 = $[108];
  }
  let t34;
  if ($[113] !== activeKey || $[114] !== canDragCurations || $[115] !== curationOptions || $[116] !== isCurationReorderPending || $[117] !== onSelectCuration) {
    let t35;
    if ($[119] !== activeKey || $[120] !== canDragCurations || $[121] !== isCurationReorderPending || $[122] !== onSelectCuration) {
      t35 = option_0 => <React.Fragment key={option_0.key}>{canDragCurations ? <SortableCurationTabOption option={option_0} activeKey={activeKey} isSortingDisabled={isCurationReorderPending} onSelect={key_1 => onSelectCuration(getCurationIdFromTabKey(key_1))} /> : <DesktopTabOption option={option_0} activeKey={activeKey} onSelect={key_2 => onSelectCuration(getCurationIdFromTabKey(key_2))} />}</React.Fragment>;
      $[119] = activeKey;
      $[120] = canDragCurations;
      $[121] = isCurationReorderPending;
      $[122] = onSelectCuration;
      $[123] = t35;
    } else {
      t35 = $[123];
    }
    t34 = curationOptions.map(t35);
    $[113] = activeKey;
    $[114] = canDragCurations;
    $[115] = curationOptions;
    $[116] = isCurationReorderPending;
    $[117] = onSelectCuration;
    $[118] = t34;
  } else {
    t34 = $[118];
  }
  let t35;
  if ($[124] !== curationTabKeys || $[125] !== t34) {
    t35 = <SortableContext items={curationTabKeys} strategy={horizontalListSortingStrategy}>{t34}</SortableContext>;
    $[124] = curationTabKeys;
    $[125] = t34;
    $[126] = t35;
  } else {
    t35 = $[126];
  }
  let t36;
  if ($[127] !== handleCurationDragEnd || $[128] !== sortableSensors || $[129] !== t35) {
    t36 = <DndContext sensors={sortableSensors} collisionDetection={closestCenter} onDragEnd={handleCurationDragEnd}>{t35}</DndContext>;
    $[127] = handleCurationDragEnd;
    $[128] = sortableSensors;
    $[129] = t35;
    $[130] = t36;
  } else {
    t36 = $[130];
  }
  let t37;
  if ($[131] !== t33 || $[132] !== t36) {
    t37 = <div ref={desktopTabsScrollerRef} className="tw-hidden tw-min-w-0 tw-flex-1 tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300 sm:tw-block"><div className="tw-flex tw-w-auto tw-gap-x-1" role="tablist">{t33}{t36}</div></div>;
    $[131] = t33;
    $[132] = t36;
    $[133] = t37;
  } else {
    t37 = $[133];
  }
  let t38;
  if ($[134] !== onSelectCuration || $[135] !== showCreateCurationAction || $[136] !== wave) {
    t38 = showCreateCurationAction && <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 sm:tw-ml-auto"><MyStreamWaveCreateCurationAction wave={wave} onCreated={onSelectCuration} /></div>;
    $[134] = onSelectCuration;
    $[135] = showCreateCurationAction;
    $[136] = wave;
    $[137] = t38;
  } else {
    t38 = $[137];
  }
  let t39;
  if ($[138] !== t32 || $[139] !== t37 || $[140] !== t38) {
    t39 = <div className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-2 tw-@container/tabs sm:tw-px-4">{t32}{t37}{t38}</div>;
    $[138] = t32;
    $[139] = t37;
    $[140] = t38;
    $[141] = t39;
  } else {
    t39 = $[141];
  }
  return t39;
};
export default MyStreamWaveDesktopTabs;
function _temp(decision) {
  return {
    decision_time: decision.timestamp
  };
}
function _temp2(decision_1) {
  return decision_1.timestamp > Time.currentMillis();
}
function _temp3(curation_0) {
  return getCurationTabKey(curation_0.id);
}
function _temp4(scroller) {
  if (!scroller) {
    return;
  }
  const activeTabElement = scroller.querySelector("[role=\"tab\"][aria-selected=\"true\"]");
  activeTabElement?.scrollIntoView({
    block: "nearest",
    inline: "nearest"
  });
}